import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import {
  FiUsers,
  FiMenu,
  FiShoppingCart,
  FiClock,
  FiTrendingUp,
  FiArrowUpRight,
  FiChevronRight,
  FiLayers,
} from 'react-icons/fi';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

/* ------------------------------------------------------------------ */
/*  CardActions (Matches Elychat & Elyfleet)                          */
/* ------------------------------------------------------------------ */
function CardActions({ onClick, title = "Open" }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      title={title}
      className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-orange-50 hover:text-orange-600 cursor-pointer"
    >
      <FiArrowUpRight className="h-4 w-4" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  ActivityBarChart (7-Day Daily Revenue in Glowing Orange)          */
/* ------------------------------------------------------------------ */
function ActivityBarChart({ series }) {
  const [hovered, setHovered] = useState(null);
  if (!series || series.length === 0) return null;
  const maxVal = Math.max(1, ...series.map((s) => Number(s.revenue || 0)));
  const hoveredRow = hovered != null ? series[hovered] : null;

  return (
    <div>
      <div className="flex items-end gap-[7px] pt-3" style={{ height: 86 }}>
        {series.map((s, i) => {
          const val = Number(s.revenue || 0);
          const pct = Math.max((val / maxVal) * 100, 8);
          const isHighlight = i === series.length - 1;
          const isHovered = hovered === i;
          return (
            <div key={s.name + i} className="flex flex-1 flex-col items-center gap-1 min-w-0">
              <div className="flex w-full items-end justify-center" style={{ height: 60 }}>
                <div
                  className={`w-[18px] sm:w-[20px] shrink-0 cursor-pointer rounded-lg transition-all duration-200 ${
                    isHovered ? 'brightness-95 scale-105' : ''
                  }`}
                  style={{
                    height: `${pct}%`,
                    backgroundColor: isHighlight ? '#ea580c' : '#fb923c',
                    boxShadow: isHighlight ? '0 2px 8px rgba(234, 88, 12, 0.4)' : 'none',
                  }}
                  title={`${s.name} (${s.date}): ₹${val.toFixed(2)}`}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                />
              </div>
              <span
                className="text-[10px] font-semibold"
                style={{ color: isHighlight ? '#ea580c' : '#9ca3af' }}
              >
                {s.name}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-1 min-h-4 text-center text-[10px] font-medium text-gray-500">
        {hoveredRow ? (
          <span className="font-semibold text-gray-800">
            {hoveredRow.name} ({hoveredRow.date}): ₹{hoveredRow.revenue.toFixed(2)}
          </span>
        ) : (
          '7-day daily revenue'
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StatusDonutPie (Matches Elychat Signature Donut Pie)              */
/* ------------------------------------------------------------------ */
function StatusDonutPie({ statusCounts, total, onNavigate }) {
  const statuses = [
    {
      id: 'completed',
      label: 'Completed',
      count: statusCounts.Completed || 0,
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.1)',
      border: 'rgba(16, 185, 129, 0.3)',
      text: '#047857',
    },
    {
      id: 'preparing',
      label: 'Preparing',
      count: statusCounts.Preparing || 0,
      color: '#f97316',
      bg: 'rgba(249, 115, 22, 0.1)',
      border: 'rgba(249, 115, 22, 0.3)',
      text: '#c2410c',
    },
    {
      id: 'ready',
      label: 'Ready',
      count: statusCounts.Ready || 0,
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.1)',
      border: 'rgba(245, 158, 11, 0.3)',
      text: '#b45309',
    },
    {
      id: 'pending',
      label: 'Pending',
      count: statusCounts.Pending || 0,
      color: '#fb923c',
      bg: 'rgba(251, 146, 60, 0.1)',
      border: 'rgba(251, 146, 60, 0.3)',
      text: '#ea580c',
    },
  ];

  const calculatedTotal = statuses.reduce((sum, s) => sum + s.count, 0);
  const grandTotal = total || calculatedTotal;

  const size = 140;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const activeSlices = statuses.filter((s) => s.count > 0);
  let cumulativePercent = 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 w-full items-center py-1">
      {/* Centered SVG Donut Chart */}
      <div className="w-full flex items-center justify-center">
        <div
          className="relative shrink-0 cursor-pointer transition-all duration-300 hover:scale-105 group"
          onClick={() => onNavigate?.('/restaurant/pos')}
          title="View POS Orders"
        >
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="transform -rotate-90 filter drop-shadow-xs"
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="transparent"
              stroke="#f1f5f9"
              strokeWidth={strokeWidth}
            />
            {calculatedTotal > 0 &&
              activeSlices.map((s) => {
                const percent = s.count / calculatedTotal;
                const gap = activeSlices.length > 1 ? 2.5 : 0;
                const sliceLength = Math.max(0, percent * circumference - gap);
                const strokeDasharray = `${sliceLength} ${circumference - sliceLength}`;
                const strokeDashoffset = -cumulativePercent * circumference;
                cumulativePercent += percent;

                return (
                  <circle
                    key={s.id}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-300 hover:opacity-90"
                  />
                );
              })}
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-3xl font-black text-gray-900 tracking-tight leading-none">
              {grandTotal}
            </span>
            <span className="text-[10px] font-extrabold text-orange-500 uppercase tracking-wider mt-1">
              TOTAL ORDERS
            </span>
          </div>
        </div>
      </div>

      {/* Symmetrical Status Radio Cards */}
      <div className="w-full flex flex-col gap-1.5">
        {statuses.map((s) => {
          const hasData = s.count > 0;
          return (
            <div
              key={s.id}
              onClick={(e) => {
                e.stopPropagation();
                onNavigate?.('/restaurant/pos');
              }}
              className={`flex items-center justify-between px-3 py-1.5 rounded-xl border transition-all duration-200 cursor-pointer hover:translate-x-1 hover:shadow-xs active:scale-[0.99] group ${
                hasData ? '' : 'opacity-65 bg-gray-50/50 border-gray-100'
              }`}
              style={{
                backgroundColor: hasData ? s.bg : undefined,
                borderColor: hasData ? s.border : undefined,
              }}
              title={`View ${s.label} (${s.count})`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110"
                  style={{ borderColor: hasData ? s.color : '#cbd5e1' }}
                >
                  <div
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: hasData ? s.color : '#cbd5e1' }}
                  />
                </div>
                <span className="text-xs font-bold text-gray-800 truncate">{s.label}</span>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span
                  className="text-sm sm:text-base font-extrabold tracking-tight font-mono"
                  style={{ color: hasData ? s.text : '#94a3b8' }}
                >
                  {s.count}
                </span>
                <span className="text-xs font-bold text-gray-400 group-hover:translate-x-0.5 transition-transform">
                  →
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main RestaurantDashboard Component                                */
/* ------------------------------------------------------------------ */
function RestaurantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalGuests: 0,
    activeOrders: 0,
    menuItems: 0,
    todayRevenue: 0,
    completedOrders: 0,
    pendingOrders: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);

  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    if (!modulesLoaded) return;
    fetchStats();
    fetchRecentData();
    fetchRevenueData();
  }, [modulesLoaded]);

  const fetchModules = async () => {
    try {
      const response = await api.get('/api/tenant/modules');
      setEnabledModules(response.data?.enabledModules || []);
    } catch (error) {
      setEnabledModules([]);
    } finally {
      setModulesLoaded(true);
    }
  };

  const fetchStats = async () => {
    try {
      const [guestsRes, ordersRes, menuRes, billsRes] = await Promise.all([
        api.get('/api/guests/restaurant').catch(() => ({ data: [] })),
        api.get('/api/pos/orders/active').catch(() => ({ data: { orders: [] } })),
        api.get('/api/menu').catch(() => ({ data: { items: [] } })),
        enabledModules.includes('bills')
          ? api.get('/api/bills').catch(() => ({ data: { bills: [] } }))
          : Promise.resolve({ data: { bills: [] } }),
      ]);

      const orders = ordersRes.data?.orders || [];
      const activeOrders = orders.filter((o) => !['completed', 'cancelled'].includes(o.status)).length;
      const completedOrders = orders.filter((o) => o.status === 'completed').length;

      // Calculate today's revenue from bills
      const bills = billsRes.data?.bills || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayRevenue = bills
        .filter((bill) => {
          const billDate = new Date(bill.created_at);
          billDate.setHours(0, 0, 0, 0);
          return billDate.getTime() === today.getTime();
        })
        .reduce((sum, bill) => sum + parseFloat(bill.total_amount || 0), 0);

      setStats({
        totalGuests: Array.isArray(guestsRes.data) ? guestsRes.data.length : 0,
        activeOrders,
        menuItems: Array.isArray(menuRes.data?.items) ? menuRes.data.items.length : 0,
        todayRevenue,
        completedOrders,
        pendingOrders: activeOrders,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRecentData = async () => {
    try {
      const ordersRes = await api.get('/api/pos/orders/active').catch(() => ({ data: { orders: [] } }));
      const orders = ordersRes.data?.orders || [];
      const sortedOrders = orders
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 4);
      setRecentOrders(sortedOrders);
    } catch (error) {
      console.error('Failed to fetch recent data:', error);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const billsRes = enabledModules.includes('bills')
        ? await api.get('/api/bills').catch(() => ({ data: { bills: [] } }))
        : { data: { bills: [] } };
      const bills = billsRes.data?.bills || [];

      // Get last 7 days revenue
      const last7Days = [];
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);

        const dayRevenue = bills
          .filter((bill) => {
            const billDate = new Date(bill.created_at);
            billDate.setHours(0, 0, 0, 0);
            return billDate.getTime() === date.getTime();
          })
          .reduce((sum, bill) => sum + parseFloat(bill.total_amount || 0), 0);

        last7Days.push({
          name: dayNames[date.getDay()],
          revenue: dayRevenue,
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        });
      }

      setRevenueData(last7Days);

      // Get order status breakdown
      const ordersRes = await api.get('/api/pos/orders/active').catch(() => ({ data: { orders: [] } }));
      const orders = ordersRes.data?.orders || [];
      const statusCounts = {
        Preparing: orders.filter((o) => o.status === 'preparing').length,
        Ready: orders.filter((o) => o.status === 'ready').length,
        Completed: orders.filter((o) => o.status === 'completed').length,
        Pending: orders.filter((o) => o.status === 'pending').length,
      };

      setOrderStatusData([
        { name: 'Completed', value: statusCounts.Completed },
        { name: 'Preparing', value: statusCounts.Preparing },
        { name: 'Ready', value: statusCounts.Ready },
        { name: 'Pending', value: statusCounts.Pending },
      ].filter((item) => item.value > 0));
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
    }
  };

  // Convert to IST (Indian Standard Time) - Proper timezone handling
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      if (typeof dateString === 'string') {
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/-\d{2}:\d{2}$/)) {
          date = new Date(dateString + 'Z');
        } else {
          date = new Date(dateString);
        }
      } else {
        date = dateString;
      }

      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }

      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      if (typeof dateString === 'string') {
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/-\d{2}:\d{2}$/)) {
          date = new Date(dateString + 'Z');
        } else {
          date = new Date(dateString);
        }
      } else {
        date = dateString;
      }

      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }

      return date.toLocaleDateString('en-IN', {
        timeZone: 'Asia/Kolkata',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  // Calculate additional metrics
  const totalRevenue = revenueData.reduce((sum, day) => sum + day.revenue, 0);
  const avgDailyRevenue = revenueData.length > 0 ? totalRevenue / revenueData.length : 0;
  const latestTodayRevenue = revenueData[revenueData.length - 1]?.revenue || stats.todayRevenue || 0;
  const yesterdayRevenue = revenueData[revenueData.length - 2]?.revenue || 0;
  const revenueGrowth =
    yesterdayRevenue > 0
      ? (((latestTodayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100).toFixed(1)
      : null;

  const statusCounts = useMemo(() => {
    return {
      Completed: orderStatusData.find((s) => s.name === 'Completed')?.value || stats.completedOrders || 0,
      Preparing: orderStatusData.find((s) => s.name === 'Preparing')?.value || 0,
      Ready: orderStatusData.find((s) => s.name === 'Ready')?.value || 0,
      Pending: orderStatusData.find((s) => s.name === 'Pending')?.value || stats.activeOrders || 0,
    };
  }, [orderStatusData, stats.completedOrders, stats.activeOrders]);

  return (
    <Layout>
      <div className="space-y-5 pb-8 px-1 sm:px-2">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 pb-1">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl sm:text-3xl lg:text-[34px] font-extrabold tracking-tight text-gray-900 leading-tight">
                Dashboard
              </h1>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-orange-50 text-orange-700 border border-orange-200">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-500 animate-pulse" />
                Live Service
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              {user?.name ? `Welcome back, ${user.name} • ` : ''}Real-time dining, kitchen orders and revenue analytics
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/restaurant/pos')}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white shadow-xs transition-all cursor-pointer"
            >
              <FiShoppingCart className="h-4 w-4" />
              POS Terminal
            </button>
            <button
              type="button"
              onClick={() => navigate('/restaurant/kds')}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-600 border border-gray-200 hover:border-orange-200 shadow-xs transition-all cursor-pointer"
            >
              <FiLayers className="h-4 w-4" />
              Kitchen Display (KDS)
            </button>
          </div>
        </div>

        {/* ── ROW 1: Hero Banner (5 cols) + Order Status Donut (7 cols) ── */}
        <section className="grid grid-cols-1 gap-4 sm:gap-5 xl:grid-cols-12 items-stretch">
          {/* Left: Hero Dining Photo Card */}
          <div className="xl:col-span-5 flex flex-col">
            <div
              onClick={() => navigate('/restaurant/pos')}
              className="group relative overflow-hidden rounded-[20px] border border-gray-200/80 bg-gray-100 shadow-xs cursor-pointer transition-all duration-300 hover:shadow-md aspect-[3/2] w-full h-full min-h-[220px] sm:min-h-[260px]"
              title="Open POS Terminal"
            >
              <img
                src="/dashboard_hero.jpg"
                alt="Restaurant Dining Ambiance"
                className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </div>
          </div>

          {/* Right: Signature Order Status Donut Card */}
          <div className="xl:col-span-7 flex flex-col">
            <div className="rounded-[20px] bg-white p-5 sm:p-6 shadow-xs border border-gray-100 flex flex-col justify-between h-full hover:border-orange-100 transition-all">
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                      <FiLayers className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Order Status Breakdown</h3>
                      <p className="text-[11px] text-gray-400">
                        {stats.activeOrders + stats.completedOrders} orders recorded today
                      </p>
                    </div>
                  </div>
                  <CardActions onClick={() => navigate('/restaurant/pos')} title="View Active Orders" />
                </div>
              </div>

              <div className="my-auto py-2">
                <StatusDonutPie
                  statusCounts={statusCounts}
                  total={stats.activeOrders + stats.completedOrders}
                  onNavigate={(url) => navigate(url)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── ROW 2: 4 Dynamic KPI Cards Grid ── */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {/* CARD 1: Today's Revenue */}
          <div
            onClick={() =>
              navigate(enabledModules.includes('bills') ? '/restaurant/bills' : '/restaurant/analytics')
            }
            className="rounded-[20px] bg-white p-5 shadow-xs border border-gray-100 hover:border-orange-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between"
            title="View Bills & Revenue"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                    <FiTrendingUp className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Today's Revenue</h3>
                    <p className="text-[11px] text-gray-400">From completed orders</p>
                  </div>
                </div>
                <CardActions
                  onClick={() =>
                    navigate(enabledModules.includes('bills') ? '/restaurant/bills' : '/restaurant/analytics')
                  }
                  title="View Revenue"
                />
              </div>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">
                  ₹{stats.todayRevenue.toFixed(2)}
                </span>
                {revenueGrowth && parseFloat(revenueGrowth) !== 0 && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      parseFloat(revenueGrowth) >= 0
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-rose-50 text-rose-700'
                    }`}
                  >
                    {parseFloat(revenueGrowth) >= 0 ? '+' : ''}
                    {revenueGrowth}% vs yest.
                  </span>
                )}
              </div>
            </div>

            <ActivityBarChart series={revenueData} />
          </div>

          {/* CARD 2: Active Orders */}
          <div
            onClick={() => navigate('/restaurant/pos')}
            className="rounded-[20px] bg-white p-5 shadow-xs border border-gray-100 hover:border-amber-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between"
            title="View POS Orders"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                    <FiShoppingCart className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Active Orders</h3>
                    <p className="text-[11px] text-gray-400">Currently in progress</p>
                  </div>
                </div>
                <CardActions onClick={() => navigate('/restaurant/pos')} title="View Orders" />
              </div>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">
                  {stats.activeOrders.toLocaleString()}
                </span>
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                  {stats.activeOrders > 0 ? 'Live in service' : 'All clear'}
                </span>
              </div>
            </div>

            <div className="w-full pt-4">
              <div className="flex items-center justify-between text-[11px] font-semibold text-gray-600 mb-1.5">
                <span>Kitchen Load</span>
                <span className="text-amber-600 font-bold">{stats.activeOrders} active</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden flex">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, stats.activeOrders * 20 || 5)}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Active kitchen & table orders</p>
            </div>
          </div>

          {/* CARD 3: Total Guests */}
          <div
            onClick={() => navigate('/restaurant/guests')}
            className="rounded-[20px] bg-white p-5 shadow-xs border border-gray-100 hover:border-orange-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between"
            title="View Guests Directory"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                    <FiUsers className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Total Guests</h3>
                    <p className="text-[11px] text-gray-400">Registered diners</p>
                  </div>
                </div>
                <CardActions onClick={() => navigate('/restaurant/guests')} title="View Guests" />
              </div>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">
                  {stats.totalGuests.toLocaleString()}
                </span>
                <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold text-orange-700">
                  Directory
                </span>
              </div>
            </div>

            <div className="w-full pt-4">
              <div className="flex items-center justify-between text-[11px] font-semibold text-gray-600 mb-1.5">
                <span>Guest Records</span>
                <span className="text-orange-600 font-bold">100% Synced</span>
              </div>
              <div className="h-2 w-full rounded-full bg-orange-100/70 overflow-hidden flex">
                <div className="h-full bg-orange-500 rounded-full w-[85%]" />
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Verified guest database</p>
            </div>
          </div>

          {/* CARD 4: Menu Items & Growth */}
          <div
            onClick={() => navigate('/restaurant/menu')}
            className="rounded-[20px] bg-white p-5 shadow-xs border border-gray-100 hover:border-orange-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between"
            title="View Menu Management"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                    <FiMenu className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Menu & Growth</h3>
                    <p className="text-[11px] text-gray-400">Items & completion rate</p>
                  </div>
                </div>
                <CardActions onClick={() => navigate('/restaurant/menu')} title="View Menu" />
              </div>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">
                  {stats.menuItems.toLocaleString()}
                </span>
                <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold text-orange-700">
                  {stats.completedOrders > 0 && stats.activeOrders > 0
                    ? `${Math.round(
                        (stats.completedOrders / (stats.completedOrders + stats.activeOrders)) * 100
                      )}% comp.`
                    : '100% comp.'}
                </span>
              </div>
            </div>

            <div className="w-full pt-4">
              <div className="flex items-center justify-between text-[11px] font-semibold text-gray-600 mb-1.5">
                <span>Fulfillment Rate</span>
                <span className="text-orange-600 font-bold">
                  {stats.completedOrders > 0 && stats.activeOrders > 0
                    ? `${Math.round(
                        (stats.completedOrders / (stats.completedOrders + stats.activeOrders)) * 100
                      )}%`
                    : '0%'}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden flex">
                <div
                  className="h-full bg-orange-500 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      stats.completedOrders > 0 && stats.activeOrders > 0
                        ? Math.round(
                            (stats.completedOrders / (stats.completedOrders + stats.activeOrders)) * 100
                          )
                        : 10
                    }%`,
                  }}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Active dishes in catalogue</p>
            </div>
          </div>
        </section>

        {/* ── ROW 3: Recent Orders (5 cols) + Revenue Trend & Analytics (7 cols) ── */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 items-stretch">
          {/* Left: Recent Orders Card */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="rounded-[20px] bg-white p-5 sm:p-6 shadow-xs border border-gray-100 flex flex-col justify-between h-full hover:border-orange-100 transition-all">
              <div>
                <div className="flex items-start justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                      <FiClock className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Recent Orders</h3>
                      <p className="text-[11px] text-gray-400">Live kitchen & table queue</p>
                    </div>
                  </div>
                  <CardActions onClick={() => navigate('/restaurant/pos')} title="View All Orders" />
                </div>

                <div className="pt-4">
                  {recentOrders && recentOrders.length > 0 ? (
                    <div className="space-y-3">
                      {recentOrders.map((order) => {
                        const status = String(order.status || '').toLowerCase();
                        const statusPill =
                          status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : status === 'preparing'
                            ? 'bg-orange-50 text-orange-700 border-orange-200'
                            : status === 'ready'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-gray-100 text-gray-700 border-gray-200';

                        return (
                          <div
                            key={order.id}
                            onClick={() => navigate('/restaurant/pos')}
                            className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-orange-50/40 hover:border-orange-200/80 transition-all duration-200 cursor-pointer group"
                          >
                            <div className="flex items-center gap-3 min-w-0 pr-2">
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-gray-200 text-xs font-mono font-bold text-gray-700 shrink-0">
                                #{order.id?.slice(-4) || 'N/A'}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-xs text-gray-900">
                                    Table {order.tables?.table_number || order.table_number || 'N/A'}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold border capitalize ${statusPill}`}
                                  >
                                    {order.status}
                                  </span>
                                </div>
                                <div className="text-[11px] text-gray-500 font-mono font-semibold mt-0.5">
                                  ₹{parseFloat(order.total || 0).toFixed(2)}
                                </div>
                              </div>
                            </div>

                            <div className="text-right shrink-0">
                              <div className="text-xs font-semibold text-gray-700 font-mono">
                                {formatDate(order.created_at)}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {formatDateShort(order.created_at)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-12 flex flex-col items-center justify-center text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-400 mb-2">
                        <FiClock className="h-6 w-6" />
                      </div>
                      <p className="text-xs font-bold text-gray-700">No recent orders</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Orders sent to POS or Kitchen will appear here
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/restaurant/pos')}
                  className="w-full py-2 rounded-xl text-xs font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  Open Kitchen & Orders Queue
                  <FiChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Right: Revenue Trend & Analytics Card */}
          <div className="lg:col-span-7 flex flex-col">
            <div className="rounded-[20px] bg-white p-5 sm:p-6 shadow-xs border border-gray-100 flex flex-col justify-between h-full hover:border-orange-100 transition-all">
              <div>
                <div className="flex items-start justify-between pb-3 border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                      <FiTrendingUp className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Revenue & Analytics</h3>
                      <p className="text-[11px] text-gray-400">7-day financial sales trend</p>
                    </div>
                  </div>
                  <CardActions
                    onClick={() =>
                      navigate(enabledModules.includes('bills') ? '/restaurant/bills' : '/restaurant/analytics')
                    }
                    title="View Analytics"
                  />
                </div>

                {/* Quick Metrics Bar */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 my-4">
                  <div className="bg-orange-50/70 border border-orange-100/80 rounded-xl p-3">
                    <span className="text-[11px] font-semibold text-orange-800">Today's Revenue</span>
                    <div className="text-lg font-extrabold text-gray-900 font-mono mt-0.5">
                      ₹{stats.todayRevenue.toFixed(2)}
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                    <span className="text-[11px] font-medium text-gray-500">7-Day Average</span>
                    <div className="text-lg font-bold text-gray-900 font-mono mt-0.5">
                      ₹{avgDailyRevenue.toFixed(2)}
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 col-span-2 sm:col-span-1 flex flex-col justify-center">
                    <span className="text-[11px] font-medium text-gray-500">Day-over-Day</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {revenueGrowth && parseFloat(revenueGrowth) !== 0 ? (
                        <span
                          className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            parseFloat(revenueGrowth) >= 0
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                          }`}
                        >
                          {parseFloat(revenueGrowth) >= 0 ? '↑' : '↓'} {Math.abs(parseFloat(revenueGrowth))}% vs yest.
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-gray-400">Stable</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Revenue Trend Area Chart */}
                {revenueData && revenueData.length > 0 ? (
                  <div className="mt-2">
                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Daily Revenue Performance
                    </div>
                    <div className="w-full" style={{ height: 180 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="revenueOrangeGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f97316" stopOpacity={0.35} />
                              <stop offset="95%" stopColor="#f97316" stopOpacity={0.0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis
                            dataKey="name"
                            stroke="#94a3b8"
                            fontSize={11}
                            tickLine={false}
                            tick={{ fill: '#94a3b8' }}
                          />
                          <YAxis
                            stroke="#94a3b8"
                            fontSize={10}
                            tickLine={false}
                            tick={{ fill: '#94a3b8' }}
                            tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #fed7aa',
                              borderRadius: '12px',
                              fontSize: '12px',
                              boxShadow: '0 4px 14px rgba(249, 115, 22, 0.1)',
                            }}
                            formatter={(value) => [`₹${parseFloat(value || 0).toFixed(2)}`, 'Revenue']}
                            labelFormatter={(label, payload) => {
                              if (payload && payload[0] && payload[0].payload) {
                                return `${payload[0].payload.name} (${payload[0].payload.date})`;
                              }
                              return label;
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="revenue"
                            stroke="#ea580c"
                            strokeWidth={2.5}
                            fillOpacity={1}
                            fill="url(#revenueOrangeGrad)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="py-10 text-center text-xs text-gray-400">
                    No revenue records recorded in the last 7 days
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-gray-100 mt-4 flex items-center justify-between text-xs text-gray-500">
                <span>7-day rolling revenue summary</span>
                <button
                  type="button"
                  onClick={() =>
                    navigate(enabledModules.includes('bills') ? '/restaurant/bills' : '/restaurant/analytics')
                  }
                  className="font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1 cursor-pointer"
                >
                  Detailed Analytics <FiChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default RestaurantDashboard;
